import {
  Button,
  IconButton,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Table,
  Tag,
  Tooltip,
} from '@dev-spendesk/grapes';
import { useEffect, useState } from 'react';

import { useTranslation } from 'common/hooks/useTranslation';
import { useIsAnalyticalSplitActivated } from 'modules/bookkeep/apis/analytical-split/useAnalyticalSplitStatus';
import { useFeature } from 'src/core/common/hooks/useFeature';
import FEATURES from 'src/core/constants/features';
import { useAccountingSoftwareName } from 'src/core/modules/accounting-integration/apis';
import { type SyncMode } from 'src/core/modules/accounting-integration/apis/useSetSyncSettings';
import { CurrentMapping } from 'src/core/modules/accounting-integration/components/CurrentMapping';
import { MultiEntityHubSettingsMarketingBanner } from 'src/core/modules/organisation-reporting';

import { MappedValue } from './MappedValue';
import { ApprovalByDimensionsActivationBanner } from '../../../../approval-by-dimensions/ApprovalByDimensionsActivationBanner';
import { ReviewLineSplitSettingsWarning } from '../../../components/ReviewLineSplitSettingsWarning/ReviewLineSplitSettingsWarning';
import { ReviewSyncSettingsWarning } from '../../../components/ReviewSyncSettingsWarning';
import type { CostCenter, CostCenterWithBudgets } from '../../costCenter';
import type { Member } from '../../member';
import { CostCenterEmptyState } from '../CostCenterEmptyState';
import './CostCentersPage.css';

type Props = {
  members: Member[];
  costCenters: CostCenterWithBudgets[];
  externalValues: Map<string, string | undefined>;
  externalFieldName: string | undefined;
  syncMode?: SyncMode;
  isSyncOperationInProgress?: boolean;
  isMapSyncEnabled: boolean;
  isIntegrationNameLoading: boolean;
  showWarningCallout: boolean;
  showLineSplitWarningCallout: boolean;
  lineSplitConfiguration:
    | { sendOnHeader: boolean; sendOnLine: boolean }
    | undefined;
  onClickFirstCreate(): void;
  onClickCreate(): void;
  onClickMoreInfo(): void;
  onClickEdit(costCenter: CostCenter): void;
  onClickDelete(costCenter: CostCenterWithBudgets): void;
  onClickImport(): void;
  onReviewSettingsClick(): void;
  onReviewLineSplitSettingsClick(): void;
};

export const CostCentersPage = ({
  members,
  costCenters,
  externalValues,
  externalFieldName,
  syncMode,
  isSyncOperationInProgress,
  isMapSyncEnabled,
  isIntegrationNameLoading,
  showWarningCallout,
  showLineSplitWarningCallout,
  lineSplitConfiguration,
  onClickFirstCreate,
  onClickCreate,
  onClickMoreInfo,
  onClickEdit,
  onClickDelete,
  onClickImport,
  onReviewSettingsClick,
  onReviewLineSplitSettingsClick,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();
  const isAnalyticalSplitActivated = useIsAnalyticalSplitActivated();
  const isMultyEntityConfigFeatureEnabled = useFeature(
    FEATURES.MULTI_ENTITY_CONFIG,
  );
  const isMultidimensionalApprovalWorkflowEnabled = useFeature(
    FEATURES.MULTIDIMENSIONAL_APPROVAL_WORKFLOW,
  );

  const [isAddDisabled, setIsAddDisabled] = useState(false);

  useEffect(() => {
    setIsAddDisabled(!!syncMode && syncMode === 'pull');
  }, [syncMode]);

  const handleReviewSyncSettingsClick = () => {
    onReviewSettingsClick();
  };

  if (costCenters.length === 0) {
    return <CostCenterEmptyState onCreateClick={onClickFirstCreate} />;
  }

  return (
    <>
      <div className="mb-16 flex w-full items-center justify-between">
        <div>
          <div>
            <div className="flex items-center gap-8">
              <div className="heading-l">{t('costCenters.costCenter')}</div>
              {isAnalyticalSplitActivated ? (
                <Tag className="ml-8" variant="purple">
                  {t('costCenters.splittable')}
                </Tag>
              ) : null}
            </div>
            <p className="text-content-secondary-bg-primary mt-8">
              {isAnalyticalSplitActivated
                ? t('costCenters.splittableCostCenterDescription')
                : t('costCenters.costCenterDescription')}
            </p>
          </div>
          <button
            type="button"
            className="!bg-background-primary-default text-content-secondary-bg-primary body-m mt-0 cursor-pointer border-none p-0 underline"
            onClick={onClickMoreInfo}
          >
            {t('costCenters.moreInformation')}
          </button>
        </div>
        <div className="flex items-center gap-4">
          <DropdownMenu
            options={[
              {
                key: 'create',
                label: t('costCenters.addCostCenterDropDown.createLabel'),
              },
              {
                key: 'import',
                label: t('costCenters.addCostCenterDropDown.importLabel'),
              },
            ]}
            placement="bottom-end"
            renderButton={(getToggleButtonProps) => (
              <Button
                {...getToggleButtonProps()}
                text={t('costCenters.addCostCenterDropDown.text')}
                variant="primaryBrand"
                isDisabled={isAddDisabled}
              />
            )}
            renderOption={(option) => <DropdownItem label={option.label} />}
            onSelect={(option) => {
              switch (option.key) {
                case 'import':
                  onClickImport();
                  break;
                case 'create':
                  onClickCreate();
                  break;
                default:
                  break;
              }
            }}
          />
          {isMapSyncEnabled && (
            <IconButton
              onClick={handleReviewSyncSettingsClick}
              variant="secondaryNeutral"
              iconName="gear"
              aria-label="Sync Settings"
            />
          )}
        </div>
      </div>

      {!isMultidimensionalApprovalWorkflowEnabled && (
        <ApprovalByDimensionsActivationBanner className="mb-16" />
      )}
      {showWarningCallout && (
        <ReviewSyncSettingsWarning onClick={handleReviewSyncSettingsClick} />
      )}
      {showLineSplitWarningCallout && lineSplitConfiguration && (
        <ReviewLineSplitSettingsWarning
          onClick={onReviewLineSplitSettingsClick}
          lineSplitConfiguration={lineSplitConfiguration}
        />
      )}

      {isMapSyncEnabled && (
        <CurrentMapping
          isLoading={isIntegrationNameLoading}
          fieldName={externalFieldName}
          syncMode={syncMode}
          isSyncOperationInProgress={isSyncOperationInProgress}
          isWrapped
        />
      )}

      <MultiEntityHubSettingsMarketingBanner className="mb-16" />
      <Table
        getIsRowDisabled={(row) =>
          row.isMultiEntity && isMultyEntityConfigFeatureEnabled
        }
        columns={[
          {
            id: 'costCenter',
            header: t('costCenters.costCenter'),
            width: '50%',
            renderCell: (row) => row.name,
          },
          ...(isMapSyncEnabled && syncMode !== 'none' && externalFieldName
            ? [
                {
                  id: 'externalName',
                  header: t('misc.integrationValue', {
                    integration: accountingSoftwareName,
                  }),
                  width: '50%',
                  renderCell: (row: CostCenterWithBudgets) => {
                    const externalValue = externalValues.get(row.id);

                    return <MappedValue value={externalValue} />;
                  },
                },
              ]
            : []),
          ...(isMultyEntityConfigFeatureEnabled
            ? [
                {
                  id: 'type',
                  header: t('multiEntityHub.costCenters.table.header.type'),
                  width: '50%',
                  renderCell: (row: CostCenterWithBudgets) => {
                    return (
                      <Tag
                        variant={row.isMultiEntity ? 'purple' : 'ocean'}
                        iconName={
                          row.isMultiEntity
                            ? 'buildings-office'
                            : 'building-storefront'
                        }
                      >
                        {row.isMultiEntity
                          ? t(
                              'multiEntityHub.costCenters.table.type.cross-entity',
                            )
                          : t(
                              'multiEntityHub.costCenters.table.type.entity-specific',
                            )}
                      </Tag>
                    );
                  },
                },
              ]
            : []),
          {
            id: 'owner',
            header: t('costCenters.owner'),
            width: '50%',
            renderCell: (row, { isRowHovered }) => {
              const owner = members.find((member) => member.id === row.ownerId);
              return (
                <div className="CostCentersPage__owner-cell">
                  {owner && (
                    <>
                      <Avatar
                        src={owner.avatar}
                        text={owner.isPending ? owner.email : owner.fullname}
                        className="CostCentersPage__owner-cell-avatar"
                      />
                      <div className="CostCentersPage__owner-cell__text">
                        {owner.isPending ? owner.email : owner.fullname}
                      </div>
                    </>
                  )}
                  {isRowHovered && (
                    <Tooltip
                      isDisabled={!row.isMultiEntity}
                      content={t(
                        'costCenters.multiEntityCostCenterTooltipRestriction',
                      )}
                    >
                      <div className="CostCentersPage__owner-cell__actions">
                        <IconButton
                          iconName="pen"
                          isDisabled={row.isMultiEntity}
                          variant="tertiaryNeutral"
                          onClick={() => {
                            onClickEdit(row);
                          }}
                          hasNegativeMargins
                          aria-label={t('misc.edit')}
                        />
                        <IconButton
                          iconName="trash"
                          isDisabled={row.isMultiEntity}
                          variant="tertiaryNeutral"
                          onClick={() => {
                            onClickDelete(row);
                          }}
                          hasNegativeMargins
                          aria-label={t('misc.delete')}
                        />
                      </div>
                    </Tooltip>
                  )}
                </div>
              );
            },
          },
        ]}
        data={costCenters}
        rowHeight="compact"
        getRowId={(row) => row.id}
      />
    </>
  );
};
